import React from 'react';
import {
    FaStar
} from 'react-icons/fa';
import CircularScoreMeter from './CircularScoreMeter';
import styles from '../ListingScore.module.css';

const ResultsSection = React.memo(({ productData, onUpgradeListing }) => {
    const safeArray = (arr) => Array.isArray(arr) ? arr : [];
    
    // Calculate component performance percentages
    const getComponentPercentage = (score, maxScore) => {
        return Math.min(100, Math.max(0, (score / maxScore) * 100));
    };

    const getScoreColor = (score, maxScore) => {
        const percentage = (score / maxScore) * 100;
        if (percentage >= 80) return '#a1cfa1ff';
        if (percentage >= 60) return '#faca9cff';
        return '#e39090ff';
    };

    const titleScore = productData.analysis.component_scores.title?.score || 0;
    const titleMax = productData.analysis.component_scores.title?.max_score || 2;
    const imagesScore = productData.analysis.component_scores.images?.score || 0;
    const imagesMax = productData.analysis.component_scores.images?.max_score || 3;
    const aplusScore = productData.analysis.component_scores.a_plus_content?.score || 0;
    const aplusMax = productData.analysis.component_scores.a_plus_content?.max_score || 3;
    const descriptionScore = productData.analysis.component_scores.description?.score || 0;
    const descriptionMax = productData.analysis.component_scores.description?.max_score || 2;
    const bulletsScore = productData.analysis.component_scores.bullet_points?.score || 0;
    const bulletsMax = productData.analysis.component_scores.bullet_points?.max_score || 1;
    const additionalScore = productData.analysis.component_scores.additional_content?.score || 0;
    const additionalMax = productData.analysis.component_scores.additional_content?.max_score || 1;

    const avgBulletChars = Math.floor(
        safeArray(productData.bullet_points).reduce((sum, b) => sum + b.length, 0) /
        (safeArray(productData.bullet_points).length || 1)
    );

    // Whether this listing has A+ content (scored on A+, not description)
    const ap = productData.a_plus_content;
    const hasAplus = !!ap && (
        ap.has_a_plus_content === true
        || safeArray(ap.modules).length > 0
        || (typeof ap.body === 'string' && ap.body.trim().length > 0)
    );

    return (
        <section className={styles.newResultsSection}>
            {/* Left Column */}
            <div className={styles.leftColumn}>
                {/* Product Card */}
                <div className={styles.productCard}>
                    <div className={styles.productCardContent}>
                        {productData.main_image && (
                            <div className={styles.productImageWrapper}>
                                <img 
                                    src={productData.main_image} 
                                    alt="Product"
                                    className={styles.productImage}
                                />
                            </div>
                        )}
                        <div className={styles.productInfo}>
                            <div className={styles.productTitle}>
                                {productData.title}
                            </div>
                            <div className={styles.asinBadge}>
                                <span className={styles.asinText}>{productData.asin}</span>
                                <a 
                                    href={`https://www.amazon.in/dp/${productData.asin}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.asinLink}
                                >
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.1521 0.199252H8.75283C8.43127 0.199252 8.1696 0.460925 8.1696 0.782488C8.1696 1.10405 8.43127 1.36572 8.75283 1.36572H10.2077L5.71042 5.86297C5.48194 6.09145 5.48194 6.45923 5.71042 6.6877C5.82368 6.80097 5.97339 6.85954 6.12182 6.85954C6.27024 6.85954 6.41995 6.80291 6.53322 6.6877L11.0305 2.19045V3.49037C11.0305 3.81193 11.2921 4.07361 11.6137 4.07361C11.9353 4.07361 12.1969 3.81193 12.1969 3.49037V1.24591C12.1969 0.66919 11.727 0.199219 11.1502 0.199219L11.1521 0.199252Z" fill="#5488C7" stroke="#F7FCFF" strokeWidth="0.4"/>
                                    <path d="M11.615 5.3052C11.2934 5.3052 11.0317 5.56687 11.0317 5.88844V9.26087C11.0317 10.236 10.2382 11.0307 9.26185 11.0307H3.13654C2.16144 11.0307 1.36667 10.2373 1.36667 9.26087V3.13556C1.36667 2.16046 2.16015 1.36569 3.13654 1.36569H6.50898C6.83054 1.36569 7.09221 1.10402 7.09221 0.782455C7.09221 0.460892 6.83054 0.199219 6.50898 0.199219H3.13654C1.51833 0.199219 0.200195 1.5154 0.200195 3.13556V9.26287C0.200195 10.8811 1.51638 12.1992 3.13654 12.1992H9.26385C10.8821 12.1992 12.2002 10.883 12.2002 9.26287V5.89044C12.2002 5.56887 11.9385 5.3072 11.617 5.3072L11.615 5.3052Z" fill="#5488C7" stroke="#F7FCFF" strokeWidth="0.4"/>
                                    </svg>

                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Listing Details Card */}
                <div className={styles.listingDetailsCard}>
                    <div className={styles.cardHeader}>LISTING DETAILS</div>
                    <div className={styles.detailsGrid}>
                        <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>B+ Images :</span>
                            <span className={styles.detailValue}> {safeArray(productData.b_plus_images).length}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>A+ Content : </span>
                            <span className={styles.detailValue}>
                                {hasAplus ? 'True' : 'False'}
                            </span>
                        </div>
                        <div className={styles.detailItemFull}>
                            <span className={styles.detailLabel}>Title Characters :</span>
                            <span className={styles.detailValue}> {productData.title ? productData.title.length : 0}</span>
                            </div>
                        {!hasAplus && (
                            <div className={styles.detailItemFull}>
                                <span className={styles.detailLabel}>Description Characters :</span>
                                <span className={styles.detailValue}> {productData.description ? productData.description.length : 0}</span>
                            </div>
                        )}
                        <div className={styles.detailItemFull}>
                            <span className={styles.detailLabel}>Avg Bullet point Characters :</span>
                            <span className={styles.detailValue}> {avgBulletChars}</span>
                    </div>
                        <div className={styles.detailItemFull}>
                            <span className={styles.detailLabel}>Number of Bullet Points :</span>
                            <span className={styles.detailValue}> {safeArray(productData.bullet_points).length}</span>
                        </div>
                        <div className={styles.detailItemFull}>
                            <span className={styles.detailLabel}>Product Specifications :</span>
                            <span className={styles.detailValue}> {safeArray(productData.specifications).length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Center Column - Score Card */}
            <div className={styles.centerColumn}>
                <div className={styles.scoreCard}>
                    <div className={styles.scoreMeterWrapper}>
                        <CircularScoreMeter score={productData.analysis.total_score} maxScore={10} />
                    </div>
                    <div className={styles.summarySection}>
                        <div className={styles.summaryBadge}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g opacity="0.7">
                        <path d="M8.77385 7.21665C5.88378 6.564 5.43611 6.11632 4.78349 3.22616C4.75359 3.09406 4.63601 3 4.50014 3C4.36428 3 4.2467 3.09406 4.2168 3.22616C3.56388 6.11632 3.11651 6.564 0.226444 7.21665C0.0940614 7.24684 0 7.36413 0 7.5C0 7.63587 0.0940614 7.75316 0.226444 7.78335C3.11651 8.43629 3.56388 8.88397 4.2168 11.7738C4.2467 11.9059 4.36428 12 4.50014 12C4.63601 12 4.75359 11.9059 4.78349 11.7738C5.43641 8.88397 5.88378 8.43629 8.77385 7.78335C8.90623 7.75316 9 7.63587 9 7.5C9 7.36413 8.90594 7.24684 8.77385 7.21665Z" fill="url(#paint0_linear_399_2945)"/>
                        <path d="M11.7724 2.3397C10.2262 1.99055 10.0092 1.77347 9.66001 0.2276C9.62963 0.0943708 9.51159 0 9.37485 0C9.23812 0 9.12008 0.0943708 9.0897 0.2276C8.74055 1.77347 8.52347 1.99055 6.9776 2.3397C6.84437 2.37008 6.75 2.48812 6.75 2.62485C6.75 2.76159 6.84437 2.87963 6.9776 2.91001C8.52347 3.25915 8.74055 3.47624 9.0897 5.0224C9.12008 5.15534 9.23812 5.25 9.37485 5.25C9.51159 5.25 9.62963 5.15534 9.66001 5.0224C10.0092 3.47624 10.2262 3.25915 11.7724 2.91001C11.9053 2.87963 12 2.76159 12 2.62485C12 2.48812 11.9053 2.37008 11.7724 2.3397Z" fill="url(#paint1_linear_399_2945)"/>
                        </g>
                        <defs>
                        <linearGradient id="paint0_linear_399_2945" x1="-3.35276e-08" y1="7.5" x2="8.26071" y2="12" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#2766AA"/>
                        <stop offset="1" stopColor="#0752B0"/>
                        </linearGradient>
                        <linearGradient id="paint1_linear_399_2945" x1="6.75" y1="2.625" x2="11.5688" y2="5.25" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#2766AA"/>
                        <stop offset="1" stopColor="#0752B0"/>
                        </linearGradient>
                        </defs>
                        </svg>
                            <span className={styles.summaryBadgeText}>SUMMARY</span>
                        </div>
                        <div className={styles.summaryText}>
                            {productData.analysis.overall_analysis}
                        </div>
                    </div>
                    <button
                        className={styles.upgradeButton}
                        type="button"
                        onClick={() => {
                            if (onUpgradeListing) onUpgradeListing(productData);
                        }}
                    >
                        <FaStar className={styles.starIcon} />
                        <span>Upgrade Listing</span>
                    </button>
                </div>
            </div>

            {/* Right Column */}
            <div className={styles.rightColumn}>
                {/* Critical Gaps Card */}
                <div className={styles.criticalGapsCard}>
                    <div className={styles.cardHeaderRow}>
                        <div className={styles.cardHeader}>CRITICAL GAPS</div>
                        <div className={styles.warningIcon}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="16" height="16" rx="8" fill="url(#paint0_linear_399_2958)"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.23917 4H8.76024C9.05726 4 9.28572 4.25537 9.25222 4.55087L8.78767 8.66841C8.75923 8.92024 8.54854 9.10809 8.29569 9.10809H7.71691C7.46407 9.10809 7.25388 8.92074 7.22493 8.66993L6.74769 4.55239C6.71265 4.2569 6.94166 4 7.23917 4ZM7.99972 10.0352C8.54246 10.0352 8.98213 10.4748 8.98213 11.0176C8.98213 11.5603 8.54246 12 7.99972 12C7.45698 12 7.0173 11.5603 7.0173 11.0176C7.0173 10.4754 7.45698 10.0352 7.99972 10.0352Z" fill="white"/>
                        <defs>
                        <linearGradient id="paint0_linear_399_2958" x1="-5.96046e-08" y1="8" x2="14.6857" y2="16" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FA9696"/>
                        <stop offset="1" stopColor="#DE524A"/>
                        </linearGradient>
                        </defs>
                        </svg>
                        </div>
                    </div>
                    <div className={styles.gapsTags}>
                        {safeArray(productData.analysis.critical_gaps).map((gap, index) => (
                            <div key={index} className={styles.gapTag}>
                                {gap}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom Right - Competitive Advantages and Component Performance */}
                <div className={styles.bottomRightRow}>
                    {/* Competitive Advantages Card */}
                    <div className={styles.competitiveAdvantagesCard}>
                        <div className={styles.cardHeader}>COMPETITIVE ADVANTAGES</div>
                        <div className={styles.advantagesTags}>
                            {safeArray(productData.analysis.competitive_advantages).map((advantage, index) => (
                                <div key={index} className={styles.advantageTag}>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.3762 0.862068L12.152 4.4927C12.2044 4.59965 12.2769 4.68651 12.3645 4.75038C12.4528 4.81426 12.5576 4.8559 12.6753 4.87241L16.6769 5.43946C16.9726 5.48109 17.2375 5.60024 17.4521 5.78041C17.6675 5.9613 17.8304 6.20105 17.9223 6.48456C18.0142 6.7681 18.0235 7.05736 17.956 7.3287C17.8885 7.60075 17.7443 7.85269 17.5304 8.05942L14.6261 10.8703C14.5393 10.9543 14.4783 11.0498 14.4452 11.1531C14.4122 11.2551 14.405 11.3685 14.4259 11.4876L15.1236 15.4685C15.1745 15.7621 15.1437 16.0499 15.0381 16.3105C14.9326 16.5703 14.7553 16.7993 14.5134 16.9744C14.2722 17.1488 13.9995 17.2472 13.7188 17.2673C13.4403 17.2867 13.1575 17.2278 12.8955 17.0878L9.32515 15.1942C9.21964 15.1383 9.1091 15.1095 9 15.1095C8.8909 15.1095 8.78036 15.1375 8.67485 15.1942L5.10448 17.0878C4.84177 17.2271 4.55895 17.2859 4.28117 17.2666C4.00052 17.2465 3.72704 17.1489 3.48658 16.9737C3.2454 16.7978 3.06739 16.5689 2.96186 16.3097C2.85635 16.0499 2.82548 15.7613 2.87645 15.4678L3.57415 11.4869C3.59496 11.367 3.5885 11.2543 3.55477 11.1524C3.52103 11.0491 3.46073 10.9536 3.37388 10.8696L0.469632 8.0587C0.255723 7.85197 0.112173 7.60074 0.0439816 7.32798C-0.0234914 7.05593 -0.0141603 6.76666 0.0777172 6.48384C0.169595 6.20031 0.333247 5.95985 0.547873 5.7797C0.761782 5.60025 1.02665 5.48181 1.3231 5.43874L5.32473 4.87169C5.44245 4.85518 5.54724 4.81355 5.63554 4.74966C5.72383 4.68578 5.7956 4.59893 5.84799 4.49198L7.62382 0.861352C7.7559 0.592904 7.95041 0.377562 8.18729 0.229694C8.4256 0.0811113 8.7041 0 9.00127 0C9.29844 0 9.57694 0.0811113 9.81525 0.229694C10.0521 0.378276 10.2466 0.592187 10.3787 0.861352L10.3762 0.862068ZM11.3969 4.86242L9.62106 1.23178C9.56076 1.10976 9.47391 1.01214 9.36768 0.9461C9.26288 0.88078 9.13655 0.844891 8.99873 0.844891C8.86092 0.844891 8.73458 0.88078 8.62979 0.9461C8.52355 1.01214 8.43598 1.10904 8.37641 1.23178L6.60058 4.86242C6.48573 5.09713 6.32495 5.29022 6.12684 5.43378C5.92874 5.57735 5.69544 5.67138 5.43633 5.70798L1.43469 6.27503C1.30047 6.29441 1.1806 6.34753 1.08514 6.42792C0.990388 6.5076 0.91789 6.61598 0.875545 6.74663C0.833195 6.87727 0.828171 7.00863 0.858318 7.12921C0.887748 7.24908 0.953784 7.36248 1.05284 7.45868L3.95709 10.2688C4.14371 10.4497 4.27651 10.6622 4.35259 10.8948C4.42867 11.1287 4.44662 11.3793 4.40139 11.6348L3.7037 15.6157C3.68001 15.7513 3.69293 15.8812 3.73958 15.9961C3.78624 16.1117 3.86663 16.2136 3.97718 16.294C4.08844 16.3744 4.21117 16.4203 4.33321 16.4282C4.4581 16.4368 4.5873 16.4095 4.70862 16.3449L8.27898 14.4513C8.50939 14.3293 8.75272 14.2683 8.99676 14.2683C9.24081 14.2683 9.48414 14.3293 9.71455 14.4513L13.2849 16.3449C13.4069 16.4095 13.5354 16.4368 13.6603 16.4282C13.7823 16.4196 13.9051 16.3744 14.0163 16.294C14.1269 16.2136 14.2073 16.1102 14.2539 15.9961C14.3006 15.8812 14.3135 15.7513 14.2898 15.6157L13.5921 11.6348C13.5476 11.3793 13.5649 11.1287 13.6409 10.8948C13.7163 10.6622 13.8491 10.4497 14.0364 10.2688L16.9407 7.45868C17.0397 7.36249 17.1058 7.2498 17.1352 7.12921C17.1654 7.00862 17.1603 6.87726 17.118 6.74663C17.0756 6.61599 17.0024 6.5076 16.9084 6.42792C16.8129 6.34753 16.693 6.29441 16.5588 6.27503L12.5572 5.70798C12.2981 5.67138 12.0648 5.57734 11.8667 5.43378C11.6693 5.29023 11.5085 5.09786 11.3929 4.86242H11.3969Z" fill="#78B3F3"/>
                                    </svg>
                                    <span>{advantage}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Component Performance Card */}
                    <div className={styles.componentPerformanceCard}>
                        <div className={styles.cardHeader}>COMPONENT PERFORMANCE</div>
                        <div className={styles.componentList}>
                            <div className={styles.componentItem}>
                                <div className={styles.componentLabelRow}>
                                    <div className={styles.componentLabel}>Title Optimization</div>
                                    <div className={styles.componentScoreHover}>
                                        {titleScore}/{titleMax}
                                    </div>
                                </div>
                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBarBackground}></div>
                                    <div 
                                        className={styles.progressBarFill}
                                        style={{ 
                                            width: `${getComponentPercentage(titleScore, titleMax)}%`,
                                            backgroundColor: getScoreColor(titleScore, titleMax)
                                        }}
                                    ></div>
                                </div>
                            </div>
                            <div className={styles.componentItem}>
                                <div className={styles.componentLabelRow}>
                                    <div className={styles.componentLabel}>B+ Images</div>
                                    <div className={styles.componentScoreHover}>
                                        {imagesScore}/{imagesMax}
                                    </div>
                                </div>
                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBarBackground}></div>
                                    <div 
                                        className={styles.progressBarFill}
                                        style={{ 
                                            width: `${getComponentPercentage(imagesScore, imagesMax)}%`,
                                            backgroundColor: getScoreColor(imagesScore, imagesMax)
                                        }}
                                    ></div>
                                </div>
                            </div>
                            <div className={styles.componentItem}>
                                <div className={styles.componentLabelRow}>
                                    <div className={styles.componentLabel}>
                                        {aplusScore > 0 ? 'A+ Content' : 'Description'}
                                    </div>
                                    <div className={styles.componentScoreHover}>
                                        {aplusScore > 0 ? `${aplusScore}/${aplusMax}` : `${descriptionScore}/${descriptionMax}`}
                                    </div>
                                </div>
                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBarBackground}></div>
                                    <div 
                                        className={styles.progressBarFill}
                                        style={{ 
                                            width: `${getComponentPercentage(aplusScore > 0 ? aplusScore : descriptionScore, aplusScore > 0 ? aplusMax : descriptionMax)}%`,
                                            backgroundColor: getScoreColor(aplusScore > 0 ? aplusScore : descriptionScore, aplusScore > 0 ? aplusMax : descriptionMax)
                                        }}
                                    ></div>
                                </div>
                            </div>
                            <div className={styles.componentItem}>
                                <div className={styles.componentLabelRow}>
                                    <div className={styles.componentLabel}>Bullet Points</div>
                                    <div className={styles.componentScoreHover}>
                                        {bulletsScore}/{bulletsMax}
                                    </div>
                                </div>
                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBarBackground}></div>
                                    <div 
                                        className={styles.progressBarFill}
                                        style={{ 
                                            width: `${getComponentPercentage(bulletsScore, bulletsMax)}%`,
                                            backgroundColor: getScoreColor(bulletsScore, bulletsMax)
                                        }}
                                    ></div>
                                </div>
                            </div>
                            <div className={styles.componentItem}>
                                <div className={styles.componentLabelRow}>
                                    <div className={styles.componentLabel}>Additional Content</div>
                                    <div className={styles.componentScoreHover}>
                                        {additionalScore}/{additionalMax}
                                    </div>
                                </div>
                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBarBackground}></div>
                                    <div 
                                        className={styles.progressBarFill}
                                        style={{ 
                                            width: `${getComponentPercentage(additionalScore, additionalMax)}%`,
                                            backgroundColor: getScoreColor(additionalScore, additionalMax)
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});

ResultsSection.displayName = 'ResultsSection';

export default ResultsSection;
